<template>
    <div class="roadmap-editor" data-testid="roadmap-editor">
        <div class="row">
            <div class="col-xs-12 text-center">
                <h1 class="page-heading">{{ heading }}</h1>
            </div>
        </div>

        <div v-if="error" class="row">
            <div class="col-xs-12">
                <div class="alert alert-danger">{{ error }}</div>
            </div>
        </div>

        <form @submit.prevent="save">
            <div class="row">
                <div class="col-xs-12 col-md-6">
                    <div class="form-group">
                        <label for="roadmap-name">Roadmap Name</label>
                        <input
                            id="roadmap-name"
                            v-model="form.name"
                            type="text"
                            class="form-control"
                            placeholder="Beginner Driver Program"
                            required
                            data-testid="roadmap-name"
                        />
                    </div>
                </div>
            </div>

            <div
                v-for="(requirement, index) in form.requirements"
                :key="requirement.key"
                class="panel panel-default"
                data-testid="roadmap-lesson"
            >
                <div class="panel-heading">
                    <strong>Lesson {{ requirement.lesson_number }}</strong>
                    <button
                        type="button"
                        class="btn btn-xs btn-dc btn-red pull-right"
                        data-testid="remove-lesson"
                        @click="removeLesson(index)"
                    >
                        Remove
                    </button>
                </div>
                <div class="panel-body">
                    <div class="row">
                        <div class="col-xs-12 col-md-3">
                            <div class="form-group">
                                <label>Lesson Number</label>
                                <input
                                    v-model.number="requirement.lesson_number"
                                    type="number"
                                    min="1"
                                    class="form-control"
                                    required
                                />
                            </div>
                        </div>
                        <div class="col-xs-12 col-md-3">
                            <div class="form-group">
                                <label>Session Type</label>
                                <select
                                    v-model="requirement.session_type_id"
                                    class="form-control"
                                    required
                                >
                                    <option
                                        v-for="sessionType in sessionTypes"
                                        :key="sessionType.id"
                                        :value="sessionType.id"
                                    >
                                        {{ sessionType.name }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="col-xs-12 col-md-3">
                            <div class="form-group">
                                <label>Instructor</label>
                                <select v-model="requirement.instructor_id" class="form-control">
                                    <option :value="null">Any instructor</option>
                                    <option
                                        v-for="instructor in instructors"
                                        :key="instructor.id"
                                        :value="instructor.id"
                                    >
                                        {{ instructorName(instructor) }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="col-xs-12 col-md-3">
                            <div class="form-group">
                                <label>Report Card</label>
                                <select v-model="requirement.reportcard_type_id" class="form-control">
                                    <option :value="null">Session type default</option>
                                    <option
                                        v-for="reportcardType in reportcardTypes"
                                        :key="reportcardType.id"
                                        :value="reportcardType.id"
                                    >
                                        {{ reportcardType.name }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-xs-12 col-md-3">
                            <div class="form-group">
                                <label>Document to Sign</label>
                                <select v-model="requirement.document_template_id" class="form-control">
                                    <option :value="null">None</option>
                                    <option
                                        v-for="template in documentTemplates"
                                        :key="template.id"
                                        :value="template.id"
                                    >
                                        {{ template.name }}
                                    </option>
                                </select>
                                <p class="help-block">
                                    Sent to the student when they book this lesson.
                                </p>
                            </div>
                        </div>
                        <div class="col-xs-12 col-md-9">
                            <div class="form-group">
                                <label>Report Card Skills</label>
                                <p class="help-block">
                                    Leave every skill unchecked to grade the school's full
                                    skill list. Skills only apply to Graded report cards.
                                </p>
                                <label
                                    v-for="task in tasks"
                                    :key="task.id"
                                    class="checkbox-inline"
                                >
                                    <input
                                        type="checkbox"
                                        :checked="lessonHasTask(requirement, task.id)"
                                        @change="toggleTask(requirement, task.id)"
                                    />
                                    {{ task.name }}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row" style="margin-bottom: 2em">
                <div class="col-xs-12">
                    <button
                        type="button"
                        class="btn btn-dc"
                        data-testid="add-lesson"
                        @click="addLesson"
                    >
                        <i class="fa fa-plus"></i> Add Lesson
                    </button>
                </div>
            </div>

            <div class="row" style="margin-bottom: 3em">
                <div class="col-xs-12">
                    <button
                        type="submit"
                        class="btn btn-dc"
                        :disabled="saving || form.requirements.length === 0"
                        data-testid="save-roadmap"
                    >
                        {{ saving ? 'Saving...' : 'Save Roadmap' }}
                    </button>
                    <button type="button" class="btn btn-default" @click="onCancel()">
                        Cancel
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
let lessonKey = 0;

export default {
    name: 'RoadmapEditor',
    props: {
        roadmap: {
            type: Object,
            default: null
        },
        sessionTypes: {
            type: Array,
            default: () => []
        },
        instructors: {
            type: Array,
            default: () => []
        },
        reportcardTypes: {
            type: Array,
            default: () => []
        },
        documentTemplates: {
            type: Array,
            default: () => []
        },
        tasks: {
            type: Array,
            default: () => []
        },
        saving: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: ''
        },
        onSave: {
            type: Function,
            required: true
        },
        onCancel: {
            type: Function,
            required: true
        }
    },
    data() {
        return {
            form: {
                name: this.roadmap ? this.roadmap.name : '',
                requirements: (this.roadmap && this.roadmap.requirements
                    ? this.roadmap.requirements
                    : []
                ).map((requirement) => ({
                    key: ++lessonKey,
                    id: requirement.id,
                    lesson_number: requirement.lesson_number,
                    session_type_id: requirement.session_type_id,
                    instructor_id: requirement.instructor_id || null,
                    reportcard_type_id: requirement.reportcard_type_id || null,
                    document_template_id: requirement.document_template_id || null,
                    task_ids: requirement.task_ids ? [...requirement.task_ids] : []
                }))
            }
        };
    },
    computed: {
        heading() {
            return this.roadmap ? 'Edit Roadmap' : 'Create Roadmap';
        }
    },
    created() {
        if (this.form.requirements.length === 0) {
            this.addLesson();
        }
    },
    methods: {
        instructorName(instructor) {
            return (
                instructor.fullname ||
                [instructor.firstname, instructor.lastname].filter(Boolean).join(' ')
            );
        },
        nextLessonNumber() {
            return this.form.requirements.reduce(
                (highest, requirement) => Math.max(highest, requirement.lesson_number || 0),
                0
            ) + 1;
        },
        addLesson() {
            this.form.requirements.push({
                key: ++lessonKey,
                id: null,
                lesson_number: this.nextLessonNumber(),
                session_type_id: this.sessionTypes.length > 0 ? this.sessionTypes[0].id : null,
                instructor_id: null,
                reportcard_type_id: null,
                document_template_id: null,
                task_ids: []
            });
        },
        removeLesson(index) {
            this.form.requirements.splice(index, 1);
        },
        lessonHasTask(requirement, taskId) {
            return requirement.task_ids.includes(taskId);
        },
        toggleTask(requirement, taskId) {
            if (this.lessonHasTask(requirement, taskId)) {
                requirement.task_ids = requirement.task_ids.filter((id) => id !== taskId);
            } else {
                requirement.task_ids = [...requirement.task_ids, taskId];
            }
        },
        save() {
            this.onSave({
                name: this.form.name,
                requirements: this.form.requirements.map((requirement) => ({
                    id: requirement.id,
                    lesson_number: requirement.lesson_number,
                    session_type_id: requirement.session_type_id,
                    instructor_id: requirement.instructor_id,
                    reportcard_type_id: requirement.reportcard_type_id,
                    document_template_id: requirement.document_template_id,
                    task_ids: requirement.task_ids.length > 0 ? requirement.task_ids : null
                }))
            });
        }
    }
};
</script>
